/******************************************************************************
 * Memokid: A game to train one's memory                                      *
 * Copyright (C) 2022 Lukas Laag                                              *
 * This file is part of Memokid                                               *
 *                                                                            *
 * Memokid is free software: you can redistribute it and/or modify            *
 * it under the terms of the GNU General Public License as published by       *
 * the Free Software Foundation, either version 3 of the License, or          *
 * (at your option) any later version.                                        *
 *                                                                            *
 * Memokid is distributed in the hope that it will be useful,                 *
 * but WITHOUT ANY WARRANTY; without even the implied warranty of             *
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the              *
 * GNU General Public License for more details.                               *
 *                                                                            *
 * You should have received a copy of the GNU General Public License          *
 * along with Memokid.  If not, see <https://www.gnu.org/licenses/>.          *
 *                                                                            *
 ******************************************************************************/

import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import Button from '@mui/material/Button'
import Slider from '@mui/material/Slider'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormLabel from '@mui/material/FormLabel'
import { PLAYER_TYPE_HUMAN, PLAYER_TYPE_COMPUTER } from '../../actions/game'
import { LEVELS, log } from '../../logs'

const PlayerDialog = props => {
  log('PlayerDialog', LEVELS.INFO, props)
  const { player, onCancel, onClick } = props
  const intl = useIntl()
  const [state, setState] = useState({
    name: '',
    type: PLAYER_TYPE_HUMAN,
    level: 3,
    ...player
  })
  const { name, type, level } = state

  const handleNameChange = useCallback(event => {
    const newState = { ...state, name: event.target.value }
    setState(newState)
  }, [onClick, state])

  const handleTypeChange = useCallback((event, value) => {
    const newState = { ...state, type: value }
    setState(newState)
  }, [onClick, state])

  const handleLevelChange = useCallback((event, value) => {
    const newState = { ...state, level: value }
    setState(newState)
  }, [onClick, state])

  const handleOk = useCallback(() => onClick({ ...player, name, type, level }), [
    player, name, type, level
  ])

  return (
    <Dialog
      open
      onClose={onCancel}
    >
      <DialogTitle>
        {intl.formatMessage({ id: player ? 'settings.modifyPlayer' : 'settings.addPlayer' })}
      </DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            label={intl.formatMessage({ id: 'settings.playerLabel' })}
            name='name'
            value={name}
            onChange={handleNameChange}
            maxLength={32}
          />
          <FormControl sx={{ marginTop: 2 }}>
            <FormLabel>{intl.formatMessage({ id: 'settings.playerTypeHeader' })}</FormLabel>
            <RadioGroup name='type' value={type} onChange={handleTypeChange}>
              <FormControlLabel value={PLAYER_TYPE_HUMAN} control={<Radio />} label={intl.formatMessage({ id: 'settings.humanPlayer' })} />
              <FormControlLabel value={PLAYER_TYPE_COMPUTER} control={<Radio />} label={intl.formatMessage({ id: 'settings.computerPlayer' })} />
            </RadioGroup>
          </FormControl>
          <FormControl sx={{ marginTop: 2 }}>
            <FormLabel>{intl.formatMessage({ id: 'settings.computerLevelHeader' })}</FormLabel>
            <Slider
              sx={{ marginTop: 5 }}
              disabled={type === PLAYER_TYPE_HUMAN}
              value={level}
              min={1}
              max={5}
              step={1}
              marks
              valueLabelDisplay='on'
              onChange={handleLevelChange}
            />
          </FormControl>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
        <Button disabled={name.length === 0} onClick={handleOk} autoFocus>
          {intl.formatMessage({ id: `common.${player ? 'modify' : 'add'}` })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
PlayerDialog.displayName = 'PlayerDialog'

export default PlayerDialog
